/**
 * Specification Modal Component
 * Custom element for handling specification modals with Safari stacking context fix
 */

if (!customElements.get('specification-modal')) {
  class SpecificationModal extends HTMLElement {
    constructor() {
      super();
      this.detailsElement = null;
      this.summaryElement = null;
      this.curtainElement = null;
      this.contentElement = null;
      this.closeButton = null;
      this.navButtons = null;
      this.originalParent = null;
      this.placeholder = null;
      this.boundHandleClose = this.handleClose.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundHandleCurtainClick = this.handleCurtainClick.bind(this);
      this.boundHandleNavClick = this.handleNavClick.bind(this);
      this.boundHandleSummaryClick = this.handleSummaryClick.bind(this);
      this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    }

    connectedCallback() {
      this.detailsElement = this.querySelector('details.modal');
      if (!this.detailsElement) return;

      this.summaryElement = this.detailsElement.querySelector('summary');
      this.curtainElement = this.detailsElement.querySelector('.modal__curtain');
      this.contentElement = this.detailsElement.querySelector('.modal__content');
      this.closeButton = this.detailsElement.querySelector('.modal__close-button');
      this.navButtons = this.detailsElement.querySelectorAll('.specification__navigation-button');
      this.imagesPreloaded = false;

      this.originalParent = this.detailsElement;
      this.placeholder = document.createComment('modal-content-placeholder');

      this.bindEvents();
    }

    disconnectedCallback() {
      this.unbindEvents();
      this.moveElementsBack();
    }

    bindEvents() {
      // IMPORTANT: Intercept click on summary to prevent native details flash
      if (this.summaryElement) {
        this.summaryElement.addEventListener('click', this.boundHandleSummaryClick);
        this.summaryElement.addEventListener('mouseenter', () => this.preloadImages(), { once: true });
        this.summaryElement.addEventListener('touchstart', () => this.preloadImages(), { once: true, passive: true });
      }

      if (this.closeButton) {
        this.closeButton.addEventListener('click', this.boundHandleClose);
      }

      if (this.curtainElement) {
        this.curtainElement.addEventListener('click', this.boundHandleCurtainClick);
      }

      if (this.navButtons) {
        this.navButtons.forEach(button => {
          button.addEventListener('click', this.boundHandleNavClick);
        });
      }

      document.addEventListener('keydown', this.boundHandleKeydown);
      document.addEventListener('click', this.boundHandleDocumentClick);
    }

    unbindEvents() {
      if (this.summaryElement) {
        this.summaryElement.removeEventListener('click', this.boundHandleSummaryClick);
      }
      if (this.closeButton) {
        this.closeButton.removeEventListener('click', this.boundHandleClose);
      }
      if (this.curtainElement) {
        this.curtainElement.removeEventListener('click', this.boundHandleCurtainClick);
      }
      if (this.navButtons) {
        this.navButtons.forEach(button => {
          button.removeEventListener('click', this.boundHandleNavClick);
        });
      }
      document.removeEventListener('keydown', this.boundHandleKeydown);
      document.removeEventListener('click', this.boundHandleDocumentClick);
    }

    /**
     * Intercept summary click to prevent native details flash
     */
handleSummaryClick(event) {
  // Ignore clicks on quantity buttons
  if (event.target.closest('.upsell-quantity') || 
      event.target.name === 'plus' || 
      event.target.name === 'minus') {
    return;
  }
  
  event.preventDefault();
  
  if (this.detailsElement.open) {
    this.close();
  } else {
    this.open();
  }
}

    isDesktop() {
      return window.matchMedia('(min-width: 990px)').matches;
    }

    open() {
      // STEP 1: Hide content BEFORE opening details
      this.contentElement.style.opacity = '0';
      this.curtainElement.style.opacity = '0';
      
      // STEP 2: Now open details (content is hidden so no flash)
      this.detailsElement.open = true;
      
      // STEP 3: Move to body
      this.moveElementsToBody();
      
      // STEP 4: Add body class
      document.body.classList.add('specification-modal-open');
      document.body.style.overflow = 'hidden';
      
      // STEP 5: Fade in after a tiny delay
      requestAnimationFrame(() => {
        this.curtainElement.style.transition = 'opacity 0.2s ease-out';
        this.curtainElement.style.opacity = '1';
        
        this.contentElement.style.transition = 'opacity 0.2s ease-out';
        this.contentElement.style.opacity = '1';
      });
      
      this.updateActiveNavButton();
      this.setupCarouselIndicators();
      
      requestAnimationFrame(() => {
        const focusTarget = this.contentElement?.querySelector('.specification-modal__content');
        if (focusTarget) {
          focusTarget.focus();
        }
      });
    }

    setupCarouselIndicators() {
      const carousel = this.contentElement?.querySelector('.specification__carousel');
      const slideList = carousel?.querySelector('ul');
      if (!carousel || !slideList) return;
      
      const slides = slideList.querySelectorAll('li');
      if (slides.length <= 1) {
        carousel.classList.add('single-image');
        return;
      }
      
      let counter = carousel.querySelector('.specification__carousel-counter');
      if (!counter) {
        counter = document.createElement('div');
        counter.className = 'specification__carousel-counter';
        carousel.appendChild(counter);
      }
      
      let pagination = carousel.querySelector('.specification__carousel-pagination');
      if (!pagination) {
        pagination = document.createElement('div');
        pagination.className = 'specification__carousel-pagination';
        
        slides.forEach((_, index) => {
          const dot = document.createElement('button');
          dot.className = 'specification__carousel-dot';
          dot.setAttribute('aria-label', `Go to image ${index + 1}`);
          dot.addEventListener('click', () => {
            const slideWidth = slides[0]?.offsetWidth || 400;
            slideList.scrollTo({ left: slideWidth * index, behavior: 'smooth' });
          });
          pagination.appendChild(dot);
        });
        
        carousel.appendChild(pagination);
      }
      
      const dots = pagination.querySelectorAll('.specification__carousel-dot');
      
      const updateIndicators = () => {
        const scrollLeft = slideList.scrollLeft;
        const slideWidth = slides[0]?.offsetWidth || 400;
        const currentIndex = Math.round(scrollLeft / slideWidth);
        
        counter.textContent = `${currentIndex + 1} / ${slides.length}`;
        
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === currentIndex);
        });
        
        const isAtEnd = scrollLeft >= (slideList.scrollWidth - slideList.clientWidth - 10);
        carousel.style.setProperty('--show-right-gradient', isAtEnd ? '0' : '1');
      };
      
      updateIndicators();
      slideList.addEventListener('scroll', updateIndicators, { passive: true });
    }

    updateActiveNavButton() {
      const currentName = this.dataset.name;
      if (!currentName) return;
      
      const heading = this.contentElement?.querySelector('.specification__content-heading');
      const currentTitle = heading?.textContent?.trim();
      
      const specName = this.querySelector('.specification_name');
      const currentSpecName = specName?.textContent?.trim();
      
      const titleH3 = this.querySelector('.title.h3');
      const currentTitleH3 = titleH3?.textContent?.trim();
      
      const buttons = this.contentElement?.querySelectorAll('.specification__navigation-button');
      if (!buttons) return;
      
      buttons.forEach(button => {
        const buttonTarget = button.dataset.target;
        const buttonText = button.textContent.trim();
        
        const isActive = 
          (buttonTarget && buttonTarget !== 'undefined' && buttonTarget === currentName) ||
          buttonText === currentTitle ||
          buttonText === currentSpecName ||
          buttonText === currentTitleH3;
        
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    close() {
      this.moveElementsBack();
      
      document.body.classList.remove('specification-modal-open');
      document.body.style.overflow = '';
      
      if (this.detailsElement) {
        this.detailsElement.open = false;
      }
      
      if (this.summaryElement) {
        this.summaryElement.focus();
      }
    }

    moveElementsToBody() {
      if (!this.curtainElement || !this.contentElement) return;
      if (this.curtainElement.parentElement === document.body) return;

      this.curtainElement.parentNode.insertBefore(this.placeholder, this.curtainElement);
      
      document.body.appendChild(this.curtainElement);
      document.body.appendChild(this.contentElement);
      
      // Curtain styles
      this.curtainElement.style.position = 'fixed';
      this.curtainElement.style.inset = '0';
      this.curtainElement.style.zIndex = '99998';
      this.curtainElement.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      
      // Content base styles
      this.contentElement.style.position = 'fixed';
      this.contentElement.style.zIndex = '99999';
      this.contentElement.style.backgroundColor = 'var(--color-base-white, #fff)';
      this.contentElement.style.overflow = 'hidden';
      
      // Position based on viewport
      if (this.isDesktop()) {
        this.contentElement.style.width = '90vw';
        this.contentElement.style.height = '80vh';
        this.contentElement.style.maxHeight = '90vh';
        this.contentElement.style.top = '50%';
        this.contentElement.style.left = '50%';
        this.contentElement.style.right = 'auto';
        this.contentElement.style.bottom = 'auto';
        this.contentElement.style.transform = 'translate(-50%, -50%)';
        this.contentElement.style.padding = '5rem';
        
        if (window.matchMedia('(min-width: 1400px)').matches) {
          this.contentElement.style.maxWidth = '1600px';
        } else if (window.matchMedia('(min-width: 1200px)').matches) {
          this.contentElement.style.maxWidth = '1400px';
        } else {
          this.contentElement.style.maxWidth = '1200px';
        }
      } else {
        this.contentElement.style.top = '0';
        this.contentElement.style.left = '0';
        this.contentElement.style.right = '0';
        this.contentElement.style.bottom = '0';
        this.contentElement.style.width = '100%';
        this.contentElement.style.height = '100vh';
        this.contentElement.style.maxWidth = '100%';
        this.contentElement.style.maxHeight = '100vh';
        this.contentElement.style.transform = 'none';
        this.contentElement.style.padding = '0';
      }
    }

    moveElementsBack() {
      if (!this.curtainElement || !this.contentElement || !this.placeholder) return;
      if (this.curtainElement.parentElement !== document.body) return;
      
      if (this.placeholder.parentNode) {
        this.placeholder.parentNode.insertBefore(this.curtainElement, this.placeholder);
        this.placeholder.parentNode.insertBefore(this.contentElement, this.placeholder.nextSibling);
        this.placeholder.remove();
      }
      
      this.curtainElement.style.cssText = '';
      this.contentElement.style.cssText = '';
    }

    handleClose(event) {
      event.preventDefault();
      this.close();
    }

    handleCurtainClick(event) {
      if (event.target === this.curtainElement) {
        this.close();
      }
    }

    /**
     * Close on any click landing outside the modal panel.
     * The curtain handler alone isn't enough: once the panel is moved to <body>
     * it covers most of the viewport, so the curtain is a sliver that's easy to
     * miss, and on some layouts it doesn't receive the click at all.
     */
    handleDocumentClick(event) {
      if (!this.detailsElement?.open) return;
      if (!this.contentElement) return;

      // Ignore the click that opened the modal
      if (this.summaryElement?.contains(event.target)) return;

      if (!this.contentElement.contains(event.target)) {
        this.close();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.detailsElement?.open) {
        event.preventDefault();
        this.close();
      }
    }

    handleNavClick(event) {
      const button = event.currentTarget;
      const buttonText = button.textContent.trim();
      const targetName = button.dataset.target;
      
      if (button.classList.contains('is-active')) return;
      
      let targetModal = null;
      
      if (targetName && targetName !== 'undefined') {
        targetModal = document.querySelector(`specification-modal[data-name="${targetName}"]`);
      }
      
      if (!targetModal) {
        const allModals = document.querySelectorAll('specification-modal');
        const currentParent = this.closest('.product-upsells, .specifications, .product-upsells__item');
        let fallbackModal = null;
        
        for (const modal of allModals) {
          const modalContent = modal.contentElement || modal.querySelector('.modal__content');
          const heading = modalContent?.querySelector('.specification__content-heading');
          const modalTitle = heading?.textContent?.trim();
          const specName = modal.querySelector('.specification_name');
          const specNameText = specName?.textContent?.trim();
          const titleH3 = modal.querySelector('.title.h3');
          const titleH3Text = titleH3?.textContent?.trim();
          
          if (modalTitle === buttonText || specNameText === buttonText || titleH3Text === buttonText) {
            const modalParent = modal.closest('.product-upsells, .specifications, .product-upsells__item');
            
            if (currentParent && modalParent && currentParent === modalParent) {
              targetModal = modal;
              break;
            } else if (!fallbackModal) {
              fallbackModal = modal;
            }
          }
        }
        
        if (!targetModal && fallbackModal) {
          targetModal = fallbackModal;
        }
      }
      
      if (!targetModal || targetModal === this) return;
      
      if (typeof targetModal.preloadImages === 'function') {
        targetModal.preloadImages();
      }
      
      const targetCurtain = targetModal.querySelector('.modal__curtain');
      const targetContent = targetModal.querySelector('.modal__content');
      
      if (!targetCurtain || !targetContent) return;
      
      if (this.curtainElement?.parentElement === document.body) {
        this.curtainElement.remove();
      }
      if (this.contentElement?.parentElement === document.body) {
        this.contentElement.remove();
      }
      
      if (this.placeholder?.parentNode) {
        this.placeholder.parentNode.insertBefore(this.curtainElement, this.placeholder);
        this.placeholder.parentNode.insertBefore(this.contentElement, this.placeholder.nextSibling);
        this.placeholder.remove();
        
        this.curtainElement.style.cssText = '';
        this.contentElement.style.cssText = '';
      }
      
      if (this.detailsElement) {
        this.detailsElement.open = false;
      }
      
      this.placeholder = document.createComment('modal-content-placeholder');
      
      // Open target modal using our controlled method
      targetModal.open();
    }

    preloadImages() {
      if (this.imagesPreloaded) return;
      this.imagesPreloaded = true;

      const images = this.contentElement?.querySelectorAll('img[loading="lazy"]');
      if (!images) return;

      images.forEach(img => {
        img.loading = 'eager';
        
        if (img.srcset && !img.complete) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.imageSrcset = img.srcset;
          link.imageSizes = img.sizes || '100vw';
          document.head.appendChild(link);
        } else if (img.src && !img.complete) {
          const preloader = new Image();
          preloader.src = img.src;
        }
      });
    }
  }

  customElements.define('specification-modal', SpecificationModal);
}